import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { readStorage, useMediaQuery, writeStorage } from '../lib/hooks';

export type ThemePref = 'system' | 'light' | 'dark';
type ThemeCtx = { pref: ThemePref; resolved: 'light' | 'dark'; setPref: (p: ThemePref) => void };

const Ctx = createContext<ThemeCtx | null>(null);
const KEY = 'ers.theme';

function readPref(): ThemePref {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return readStorage<ThemePref>(KEY, 'system');
  }
}

/** Тема: системна за замовчуванням, перемикач зберігається в браузері. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)');
  const resolved = pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
    // Колір панелі браузера — з токена полотна поточної теми (без дублювання значень).
    const color = getComputedStyle(document.documentElement).getPropertyValue('--c-canvas').trim();
    if (color) document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.setAttribute('content', color));
  }, [resolved]);

  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    try {
      window.localStorage.setItem(KEY, p);
    } catch {
      writeStorage(KEY, p);
    }
  }, []);

  const value = useMemo(() => ({ pref, resolved, setPref }), [pref, resolved, setPref]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('ThemeProvider відсутній');
  return c;
}

export const THEME_LABELS: Record<ThemePref, string> = { system: 'Як у системі', light: 'Світла', dark: 'Темна' };
